<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue';
import type { AwardScene, AwardTier } from './milestone-award-scene';
import { useEventListener, useResizeObserver, useScrollLock } from '@vueuse/core';
import { CompactButton } from '@youcan/celeste';

const props = defineProps<{
  open: boolean;
  tier: AwardTier;
  icon: string;
  name: string;
  reason: string;
  earnedAt?: Date | string | null;
  owner?: string | null;
}>();

const emit = defineEmits<{
  'update:open': [value: boolean];
}>();

const { t, locale } = useI18n();

const DEG = Math.PI / 180;
const DRAG_RATIO = 0.6;
const SPIN_THRESHOLD = 2.2;
const LEAN_TURN = 14;
const LEAN_TILT = 9;

const uid = useId();
const titleId = `${uid}-title`;
const reasonId = `${uid}-reason`;

const earnedLabel = computed(() => {
  if (!props.earnedAt) {
    return null;
  }

  const date = new Date(props.earnedAt);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat(locale.value, { dateStyle: 'long' }).format(date);
});

const stage = ref<HTMLElement | null>(null);
const canvas = ref<HTMLCanvasElement | null>(null);
const closeButton = ref<ComponentPublicInstance | null>(null);
const isReady = ref(false);
const hasFailed = ref(false);

const scrollLock = useScrollLock(document.body);

const motion = {
  ry: 0,
  rx: 0,
  vy: 0,
  target: 0,
  stiffness: 0.035,
  damping: 0.3,
  mode: 'spring' as 'spin' | 'spring',
  dragging: false,
  leanX: 0,
  leanY: 0,
  leanTargetX: 0,
  leanTargetY: 0,
  last: 0,
  frame: 0,
  reduced: false,
};

let scene: AwardScene | null = null;
let session = 0;
let pointer: { id: number; x: number; y: number; startX: number; startY: number; time: number; moved: boolean } | null = null;
let restoreFocus: HTMLElement | null = null;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function springTo(target: number, stiffness = 0.035, damping = 0.3) {
  motion.mode = 'spring';
  motion.target = target;
  motion.stiffness = stiffness;
  motion.damping = damping;
}

function settle() {
  springTo(Math.round((motion.ry + motion.vy * 4) / 180) * 180);
}

function flip(direction: 1 | -1) {
  const face = motion.mode === 'spring' ? motion.target : Math.round(motion.ry / 180) * 180;

  motion.vy = 0;
  springTo(face + 180 * direction, 0.03, 0.28);
}

function step(now: number) {
  const dt = Math.min(48, now - (motion.last || now)) / 16.67;
  motion.last = now;

  if (!motion.dragging) {
    if (motion.mode === 'spin') {
      motion.ry += motion.vy * dt;
      motion.vy *= 0.955 ** dt;

      if (Math.abs(motion.vy) < SPIN_THRESHOLD) {
        settle();
      }
    }
    else {
      const acceleration = (motion.target - motion.ry) * motion.stiffness - motion.vy * motion.damping;
      motion.vy += acceleration * dt;
      motion.ry += motion.vy * dt;
    }

    motion.rx += -motion.rx * (1 - 0.9 ** dt);
  }

  const ease = 1 - 0.92 ** dt;

  motion.leanX += (motion.leanTargetX - motion.leanX) * ease;
  motion.leanY += (motion.leanTargetY - motion.leanY) * ease;

  scene?.render((motion.rx + motion.leanX) * DEG, (motion.ry + motion.leanY) * DEG, motion.reduced ? 0 : now);
  motion.frame = requestAnimationFrame(step);
}

async function start() {
  const current = ++session;

  try {
    const { createAwardScene } = await import('./milestone-award-scene');

    if (current !== session || !canvas.value) {
      return;
    }

    scene = createAwardScene(canvas.value, {
      tier: props.tier,
      earnedLabel: earnedLabel.value,
      owner: props.owner ? { caption: t('home.milestones.award.earned_by'), name: props.owner } : null,
    });
  }
  catch {
    if (current === session) {
      fail();
    }

    return;
  }

  motion.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  motion.rx = 0;
  motion.vy = 0;
  motion.leanX = 0;
  motion.leanY = 0;
  motion.leanTargetX = 0;
  motion.leanTargetY = 0;
  motion.last = 0;
  motion.dragging = false;
  motion.ry = motion.reduced ? 0 : -540;
  springTo(0, 0.01, 0.19);

  cancelAnimationFrame(motion.frame);
  motion.frame = requestAnimationFrame(step);
  isReady.value = true;
}

function stop() {
  session++;
  cancelAnimationFrame(motion.frame);
  scene?.dispose();
  scene = null;
  pointer = null;
  isReady.value = false;
}

function fail() {
  stop();
  hasFailed.value = true;
}

function onPointerDown(event: PointerEvent) {
  if (hasFailed.value) {
    return;
  }

  stage.value?.setPointerCapture(event.pointerId);
  pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, startX: event.clientX, startY: event.clientY, time: event.timeStamp, moved: false };
  motion.dragging = true;
  motion.vy = 0;
}

function onPointerMove(event: PointerEvent) {
  if (!pointer || event.pointerId !== pointer.id) {
    return;
  }

  const dx = event.clientX - pointer.x;
  const dy = event.clientY - pointer.y;
  const dt = Math.max(1, event.timeStamp - pointer.time);

  motion.ry += dx * DRAG_RATIO;
  motion.rx = clamp(motion.rx + dy * 0.35, -30, 30);
  motion.vy = motion.vy * 0.5 + ((dx * DRAG_RATIO) / dt) * 16.67 * 0.5;

  pointer.x = event.clientX;
  pointer.y = event.clientY;
  pointer.time = event.timeStamp;

  if (Math.hypot(event.clientX - pointer.startX, event.clientY - pointer.startY) > 6) {
    pointer.moved = true;
  }
}

function onPointerUp(event: PointerEvent) {
  if (!pointer || event.pointerId !== pointer.id) {
    return;
  }

  const { moved, time } = pointer;

  pointer = null;
  motion.dragging = false;

  if (!moved) {
    const rect = stage.value?.getBoundingClientRect();

    flip(rect && event.clientX < rect.left + rect.width / 2 ? -1 : 1);

    return;
  }

  if (event.timeStamp - time > 80) {
    motion.vy = 0;
  }

  if (Math.abs(motion.vy) > SPIN_THRESHOLD) {
    motion.mode = 'spin';

    return;
  }

  settle();
}

function onStageKeydown(event: KeyboardEvent) {
  if (hasFailed.value) {
    return;
  }

  if (['ArrowLeft', 'ArrowRight', 'Enter', ' '].includes(event.key)) {
    event.preventDefault();
    flip(event.key === 'ArrowLeft' ? -1 : 1);
  }
}

function close() {
  emit('update:open', false);
}

useEventListener('keydown', (event: KeyboardEvent) => {
  if (props.open && event.key === 'Escape') {
    close();
  }
});

useEventListener('pointermove', (event: PointerEvent) => {
  if (!props.open || motion.reduced || event.pointerType !== 'mouse' || !stage.value) {
    return;
  }

  const rect = stage.value.getBoundingClientRect();
  const x = clamp((event.clientX - (rect.left + rect.width / 2)) / (window.innerWidth / 2), -1, 1);
  const y = clamp((event.clientY - (rect.top + rect.height / 2)) / (window.innerHeight / 2), -1, 1);

  motion.leanTargetY = x * LEAN_TURN;
  motion.leanTargetX = y * LEAN_TILT;
});

useEventListener(document, 'mouseleave', () => {
  motion.leanTargetX = 0;
  motion.leanTargetY = 0;
});

useResizeObserver(canvas, () => scene?.resize());

useEventListener(canvas, 'webglcontextlost', () => {
  if (scene) {
    fail();
  }
});

watch(() => props.open, async (isOpen) => {
  scrollLock.value = isOpen;

  if (!isOpen) {
    stop();
    restoreFocus?.focus();
    restoreFocus = null;

    return;
  }

  restoreFocus = document.activeElement as HTMLElement | null;
  hasFailed.value = false;

  await nextTick();

  (closeButton.value?.$el as HTMLElement | undefined)?.focus();
  start();
}, { immediate: true });

onBeforeUnmount(() => {
  stop();
  scrollLock.value = false;
});
</script>

<template>
  <Teleport to="body">
    <Transition name="award" :duration="{ enter: 450, leave: 250 }">
      <div
        v-if="open"
        class="award-backdrop"
        @click.self="close"
      >
        <div
          class="award-dialog"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          :aria-describedby="reasonId"
        >
          <CompactButton
            ref="closeButton"
            type="button"
            class="award-close"
            icon="i-celeste-close-line"
            variant="ghost"
            :aria-label="t('home.milestones.award.close')"
            @click="close"
          />

          <div
            ref="stage"
            class="award-stage"
            :class="{ 'is-static': hasFailed }"
            role="img"
            :tabindex="hasFailed ? -1 : 0"
            :aria-label="name"
            @pointerdown="onPointerDown"
            @pointermove="onPointerMove"
            @pointerup="onPointerUp"
            @pointercancel="onPointerUp"
            @keydown="onStageKeydown"
          >
            <img
              v-if="hasFailed"
              class="award-fallback"
              :src="icon"
              alt=""
              draggable="false"
            >
            <canvas
              v-else
              ref="canvas"
              class="award-canvas"
              :class="{ 'is-ready': isReady }"
            />
          </div>

          <div class="award-copy">
            <h2 :id="titleId">
              {{ name }}
            </h2>
            <p :id="reasonId">
              {{ reason }}
            </p>
            <span v-if="!hasFailed" class="award-hint">{{ t('home.milestones.award.hint') }}</span>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.award-backdrop {
  display: flex;
  position: fixed;
  z-index: 1000;
  align-items: center;
  justify-content: center;
  padding: 16px;
  inset: 0;
  background: rgb(14 18 27 / 60%);
  backdrop-filter: blur(6px);
}

.award-dialog {
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  width: min(360px, 100%);
  padding: 8px 24px 28px;
  overflow: hidden;
  border: 1px solid var(--color-stroke-soft-200);
  border-radius: var(--radius-20);
  background: var(--color-bg-white-0, #fff);
  box-shadow: var(--shadow-regular-md);
  color: var(--color-text-strong-950);
  text-align: center;
}

.award-close {
  position: absolute;
  z-index: 2;
  top: var(--spacing-16);
  inset-inline-end: var(--spacing-16);
}

.award-stage {
  width: calc(100% + 48px);
  height: 320px;
  margin: 0 -24px;
  outline: none;
  cursor: grab;
  touch-action: none;
  user-select: none;
}

.award-stage:active {
  cursor: grabbing;
}

.award-stage.is-static {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
}

.award-fallback {
  width: 168px;
  height: auto;
}

.award-canvas {
  display: block;
  width: 100%;
  height: 100%;
  transition: opacity 0.4s ease;
  opacity: 0;
}

.award-canvas.is-ready {
  opacity: 1;
}

.award-copy {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.award-copy h2 {
  color: var(--color-text-strong-950);
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: 28px;
}

.award-copy p {
  max-width: 280px;
  color: var(--color-text-sub-600);
  font-size: 14px;
  line-height: 20px;
}

.award-hint {
  margin-top: 10px;
  color: var(--color-text-soft-400);
  font-size: 12px;
}

.award-enter-active,
.award-leave-active {
  transition: opacity 0.3s ease;
}

.award-enter-active .award-dialog {
  transition:
    transform 0.45s cubic-bezier(0.2, 0.9, 0.3, 1.2),
    opacity 0.3s ease;
}

.award-leave-active .award-dialog {
  transition:
    transform 0.25s ease,
    opacity 0.2s ease;
}

.award-enter-from,
.award-leave-to {
  opacity: 0;
}

.award-enter-from .award-dialog,
.award-leave-to .award-dialog {
  transform: translateY(12px) scale(0.94);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .award-enter-active .award-dialog,
  .award-leave-active .award-dialog {
    transition: opacity 0.2s ease;
  }
}
</style>
